import { useState, useEffect, Fragment } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { detecterTypeAnalyse, formatDonneesResultat } from '@/lib/analysesSpeciales';
import QRCode from 'qrcode';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import LabNavigation from '@/components/feature/LabNavigation';

interface PatientInfo {
  id: string;
  nom: string;
  prenom: string;
  date_naissance: string | null;
  sexe: string | null;
  numero_dossier: string | null;
  telephone: string | null;
}

interface MedecinInfo {
  id: string;
  nom: string;
  prenom: string;
  specialite: string | null;
}

interface LaborantinInfo {
  id: string;
  nom: string;
  prenom: string;
}

interface DossierInfo {
  id: string;
  date_analyse: string;
  statut: string;
  laborantin_id: string | null;
  bon_laboratoire_id: string | null;
  patient_id: string;
  conclusion: string | null;
  inclure_resultats_anterieurs: boolean;
  prescripteur_externe_nom: string | null;
}

interface ResultatAnterieur {
  id: string;
  valeur: number | null;
  valeur_texte: string | null;
  dossier_id: string;
  created_at: string;
  dossier_date: string;
  analyse_code: string;
  analyse_nom: string;
  analyse_unite: string;
}

interface BonLaboratoireInfo {
  id: string;
  type_analyse: string;
  date_prescription: string;
  medecin_id: string;
  medecin: MedecinInfo | null;
  sage_femme_id: string | null;
  prescripteur_type: string;
  sage_femme: { nom: string; prenom: string } | null;
}

interface AnalyseCatalogue {
  id: string;
  code: string;
  nom: string;
  unite: string;
  categorie: string;
  val_min_0_1_an: number | null;
  val_max_0_1_an: number | null;
  val_min_1_5_ans: number | null;
  val_max_1_5_ans: number | null;
  val_min_5_12_ans: number | null;
  val_max_5_12_ans: number | null;
  val_min_12_18_ans: number | null;
  val_max_12_18_ans: number | null;
  val_min_adulte_homme: number | null;
  val_max_adulte_homme: number | null;
  val_min_adulte_femme: number | null;
  val_max_adulte_femme: number | null;
  val_min_60_plus: number | null;
  val_max_60_plus: number | null;
}

interface ResultatAnalyse {
  id: string;
  valeur: number | null;
  valeur_texte: string | null;
  donnees: Record<string, string> | null;
  fichier_url: string | null;
  analyse: AnalyseCatalogue;
}

export default function RapportAnalysesPage() {
  const { dossierId } = useParams<{ dossierId: string }>();
  const [searchParams] = useSearchParams();
  const shouldPrint = searchParams.get('print') === '1';
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [dossier, setDossier] = useState<DossierInfo | null>(null);
  const [patient, setPatient] = useState<PatientInfo | null>(null);
  const [patientAge, setPatientAge] = useState<number | null>(null);
  const [bonLaboratoire, setBonLaboratoire] = useState<BonLaboratoireInfo | null>(null);
  const [laborantin, setLaborantin] = useState<LaborantinInfo | null>(null);
  const [resultats, setResultats] = useState<ResultatAnalyse[]>([]);
  const [resultatsAnterieurs, setResultatsAnterieurs] = useState<Map<string, ResultatAnterieur[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [finalizing, setFinalizing] = useState(false);
  const [finalizeMessage, setFinalizeMessage] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login', { replace: true }); return; }
    if (role !== 'laborantin' && role !== 'super_admin' && role !== 'medecin') { navigate('/', { replace: true }); return; }
    if (dossierId) loadRapport();
  }, [user, authLoading, role, dossierId]);

  // Auto-impression si demandé via ?print=1
  useEffect(() => {
    if (!loading && shouldPrint && dossier && resultats.length > 0) {
      const timer = setTimeout(() => {
        window.print();
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [loading, shouldPrint, dossier, resultats]);

  // Génération du QR code de vérification
  useEffect(() => {
    if (!dossier || !dossier.id) return;
    const generateQR = async () => {
      try {
        const baseUrl = window.location.origin;
        const verificationUrl = `${baseUrl}/verifier-document?type=resultat_analyse&id=${dossier.id}`;
        const dataUrl = await QRCode.toDataURL(verificationUrl, {
          width: 120,
          margin: 1,
          color: { dark: '#000000', light: '#ffffff' },
        });
        setQrDataUrl(dataUrl);
      } catch {
        // QR generation failed — no big deal
      }
    };
    generateQR();
  }, [dossier]);

  const loadRapport = async () => {
    setLoading(true);
    setError('');
    try {
      const { data: dossierData, error: dossierError } = await supabase
        .from('dossiers_analyses')
        .select('id, date_analyse, statut, laborantin_id, bon_laboratoire_id, patient_id, conclusion, inclure_resultats_anterieurs, prescripteur_externe_nom')
        .eq('id', dossierId)
        .single();

      if (dossierError) throw dossierError;
      if (!dossierData) throw new Error('Dossier non trouvé');
      setDossier(dossierData);

      // Patient
      const { data: patientData, error: patientError } = await supabase
        .from('patients')
        .select('id, nom, prenom, date_naissance, sexe, numero_dossier, telephone')
        .eq('id', dossierData.patient_id)
        .single();

      if (patientError) throw patientError;
      setPatient(patientData);

      if (patientData.date_naissance) {
        const birth = new Date(patientData.date_naissance);
        const today = new Date();
        let age = today.getFullYear() - birth.getFullYear();
        const m = today.getMonth() - birth.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
        setPatientAge(age);
      }

      // Laborantin
      if (dossierData.laborantin_id) {
        const { data: laboData } = await supabase
          .from('laborantins')
          .select('id, nom, prenom')
          .eq('id', dossierData.laborantin_id)
          .maybeSingle();
        if (laboData) setLaborantin(laboData);
      }

      // Bon de laboratoire et prescripteur
      if (dossierData.bon_laboratoire_id) {
        const { data: bonData } = await supabase
          .from('bons_laboratoire')
          .select('id, type_analyse, date_prescription, medecin_id, sage_femme_id, prescripteur_type')
          .eq('id', dossierData.bon_laboratoire_id)
          .maybeSingle();

        if (bonData) {
          const bonInfo: BonLaboratoireInfo = {
            id: bonData.id,
            type_analyse: bonData.type_analyse,
            date_prescription: bonData.date_prescription,
            medecin_id: bonData.medecin_id,
            medecin: null,
            sage_femme_id: bonData.sage_femme_id,
            prescripteur_type: bonData.prescripteur_type || 'medecin',
            sage_femme: null,
          };

          if (bonData.prescripteur_type === 'sage_femme' && bonData.sage_femme_id) {
            const { data: sfData } = await supabase
              .from('sage_femmes')
              .select('nom, prenom')
              .eq('id', bonData.sage_femme_id)
              .maybeSingle();
            if (sfData) bonInfo.sage_femme = sfData;
          } else if (bonData.medecin_id) {
            const { data: medData } = await supabase
              .from('medecins')
              .select('id, nom, prenom, specialite')
              .eq('id', bonData.medecin_id)
              .maybeSingle();
            if (medData) bonInfo.medecin = medData;
          }

          setBonLaboratoire(bonInfo);
        }
      } else {
        // Analyse directe (prescription externe)
        setBonLaboratoire(null);
      }

      // Résultats
      const { data: resultsData, error: resultsError } = await supabase
        .from('resultats_analyses')
        .select(`
          id, valeur, valeur_texte, donnees, fichier_url,
          analyse:analyses_biochimie_catalogue!inner(
            id, code, nom, unite, categorie,
            val_min_0_1_an, val_max_0_1_an,
            val_min_1_5_ans, val_max_1_5_ans,
            val_min_5_12_ans, val_max_5_12_ans,
            val_min_12_18_ans, val_max_12_18_ans,
            val_min_adulte_homme, val_max_adulte_homme,
            val_min_adulte_femme, val_max_adulte_femme,
            val_min_60_plus, val_max_60_plus
          )
        `)
        .eq('dossier_id', dossierId);

      if (resultsError) throw resultsError;
      setResultats((resultsData || []) as unknown as ResultatAnalyse[]);

      // Charger les resultats anterieurs si demande
      if (dossierData.inclure_resultats_anterieurs) {
        const analyseIds = (resultsData || []).map((r: any) => r.analyse_id);
        if (analyseIds.length > 0) {
          const { data: anterieursData } = await supabase
            .from('resultats_analyses')
            .select(`
              id, valeur, valeur_texte, dossier_id, created_at,
              analyse_id,
              dossiers_analyses!inner(date_analyse, patient_id)
            `)
            .eq('dossiers_analyses.patient_id', dossierData.patient_id)
            .neq('dossier_id', dossierId)
            .in('analyse_id', analyseIds)
            .order('created_at', { ascending: false })
            .limit(50);

          if (anterieursData) {
            const mapAnterieurs = new Map<string, ResultatAnterieur[]>();
            for (const r of anterieursData as any[]) {
              // Trouver l'analyse correspondante dans le catalogue
              const catAnalyse = (resultsData || []).find((cr: any) => cr.analyse_id === r.analyse_id);
              const entry: ResultatAnterieur = {
                id: r.id,
                valeur: r.valeur,
                valeur_texte: r.valeur_texte,
                dossier_id: r.dossier_id,
                created_at: r.created_at,
                dossier_date: r.dossiers_analyses?.date_analyse || r.created_at,
                analyse_code: catAnalyse?.analyse?.code || '',
                analyse_nom: catAnalyse?.analyse?.nom || '',
                analyse_unite: catAnalyse?.analyse?.unite || '',
              };
              if (!mapAnterieurs.has(r.analyse_id)) {
                mapAnterieurs.set(r.analyse_id, []);
              }
              mapAnterieurs.get(r.analyse_id)!.push(entry);
            }
            setResultatsAnterieurs(mapAnterieurs);
          }
        }
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement du rapport');
    } finally {
      setLoading(false);
    }
  };

  const finalizeDossier = async (shouldPrint: boolean) => {
    if (!dossier || !patient) return;
    setFinalizing(true);
    setFinalizeMessage('');

    try {
      // Mettre à jour le statut du dossier : brouillon → termine
      await supabase
        .from('dossiers_analyses')
        .update({ statut: 'termine' })
        .eq('id', dossier.id);

      // Mettre à jour le bon de laboratoire si présent
      if (dossier.bon_laboratoire_id) {
        await supabase
          .from('bons_laboratoire')
          .update({ statut: 'termine', date_resultat: new Date().toISOString() })
          .eq('id', dossier.bon_laboratoire_id);
      }

      // Envoyer les notifications si bon de labo (pas analyse directe)
      if (dossier.bon_laboratoire_id && bonLaboratoire) {
        try {
          const rapportUrl = `/laborantin/rapport-analyses/${dossier.id}`;
          const patientNom = `${patient.prenom} ${patient.nom}`;

          if (bonLaboratoire.prescripteur_type === 'medecin' && bonLaboratoire.medecin_id) {
            const { data: medUser } = await supabase
              .from('medecins')
              .select('user_id, prenom, nom')
              .eq('id', bonLaboratoire.medecin_id)
              .maybeSingle();
            if (medUser?.user_id) {
              await supabase.functions.invoke('send-notification', {
                body: {
                  user_id: medUser.user_id,
                  type: 'resultats_analyses',
                  titre: 'Résultats d\'analyses disponibles',
                  message: `Les résultats d'analyses pour ${patientNom} sont disponibles. Consultez le rapport.`,
                  lien: rapportUrl,
                },
              });
            }
          } else if (bonLaboratoire.prescripteur_type === 'sage_femme' && bonLaboratoire.sage_femme_id) {
            const { data: sfUser } = await supabase
              .from('sage_femmes')
              .select('user_id, prenom, nom')
              .eq('id', bonLaboratoire.sage_femme_id)
              .maybeSingle();
            if (sfUser?.user_id) {
              await supabase.functions.invoke('send-notification', {
                body: {
                  user_id: sfUser.user_id,
                  type: 'resultats_analyses',
                  titre: 'Résultats d\'analyses disponibles',
                  message: `Les résultats d'analyses pour ${patientNom} sont disponibles. Consultez le rapport.`,
                  lien: rapportUrl,
                },
              });
            }
          }

          const { data: patData } = await supabase
            .from('patients')
            .select('user_id')
            .eq('id', patient.id)
            .maybeSingle();
          if (patData?.user_id) {
            await supabase.functions.invoke('send-notification', {
              body: {
                user_id: patData.user_id,
                type: 'resultats_analyses',
                titre: 'Vos résultats d\'analyses sont disponibles',
                message: `Bonjour ${patientNom}, vos résultats d'analyses de laboratoire sont maintenant disponibles.`,
                lien: rapportUrl,
              },
            });
          }
        } catch (notifErr) {
          console.error('Erreur lors de l\'envoi des notifications:', notifErr);
        }
      }

      // Mettre à jour le statut local
      setDossier(prev => prev ? { ...prev, statut: 'termine' } : prev);

      setFinalizeMessage(shouldPrint ? 'Rapport finalisé — lancement de l\'impression...' : 'Résultats envoyés avec succès !');

      if (shouldPrint) {
        setTimeout(() => {
          window.print();
        }, 500);
      }
    } catch (err: any) {
      setFinalizeMessage('Erreur lors de la finalisation : ' + (err.message || 'Erreur inconnue'));
    } finally {
      setFinalizing(false);
    }
  };

  const getAgeRange = (age: number | null): string => {
    if (age === null) return 'adulte_homme';
    if (age < 1) return '0_1_an';
    if (age <= 5) return '1_5_ans';
    if (age <= 12) return '5_12_ans';
    if (age <= 18) return '12_18_ans';
    if (age > 60) return '60_plus';
    return 'adulte_homme';
  };

  const getValeursReference = (analyse: AnalyseCatalogue, age: number | null, sexe: string | null) => {
    const range = getAgeRange(age);
    const rangeMap: Record<string, { min: number | null; max: number | null }> = {
      '0_1_an': { min: analyse.val_min_0_1_an, max: analyse.val_max_0_1_an },
      '1_5_ans': { min: analyse.val_min_1_5_ans, max: analyse.val_max_1_5_ans },
      '5_12_ans': { min: analyse.val_min_5_12_ans, max: analyse.val_max_5_12_ans },
      '12_18_ans': { min: analyse.val_min_12_18_ans, max: analyse.val_max_12_18_ans },
      '60_plus': { min: analyse.val_min_60_plus, max: analyse.val_max_60_plus },
      'adulte_homme': {
        min: sexe === 'F' ? analyse.val_min_adulte_femme : analyse.val_min_adulte_homme,
        max: sexe === 'F' ? analyse.val_max_adulte_femme : analyse.val_max_adulte_homme,
      },
    };
    return rangeMap[range] || { min: null, max: null };
  };

  const getResultatStatut = (
    valeur: number | null,
    minRef: number | null,
    maxRef: number | null,
  ): 'normal' | 'bas' | 'haut' | 'inconnu' => {
    if (valeur === null) return 'inconnu';
    if (minRef !== null && maxRef !== null) {
      if (valeur < minRef) return 'bas';
      if (valeur > maxRef) return 'haut';
      return 'normal';
    }
    if (maxRef !== null) {
      if (valeur > maxRef) return 'haut';
      return 'normal';
    }
    if (minRef !== null) {
      if (valeur < minRef) return 'bas';
      return 'normal';
    }
    return 'inconnu';
  };

  const formaterDateLettre = (d: string | null) => {
    if (!d) return '—';
    const date = new Date(d);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const formaterDateHeure = (d: string) => {
    return new Date(d).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const getAgeLabel = (age: number | null): string => {
    if (age === null) return '—';
    if (age < 1) return `${Math.floor(age * 12)} mois`;
    return `${age} an${age > 1 ? 's' : ''}`;
  };

  // Grouper par catégorie
  const resultatsParCategorie = resultats.reduce((acc, r) => {
    const cat = r.analyse?.categorie || 'Autres';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(r);
    return acc;
  }, {} as Record<string, ResultatAnalyse[]>);

  const categoriesOrdonnees = Object.keys(resultatsParCategorie).sort();

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin mb-4 block"></i>
          <span className="text-gray-600">Chargement du rapport...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center p-8">
          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-3xl text-red-500"></i>
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Erreur</h2>
            <p className="text-gray-500 mb-4">{error}</p>
            <button
              onClick={() => navigate('/laborantin/saisir-analyses')}
              className="px-5 py-2.5 bg-teal-600 text-white rounded-full hover:bg-teal-700 transition-all text-sm font-medium cursor-pointer"
            >
              Retour à la saisie
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col print:bg-white">
      <div className="print:hidden">
        <Header />
      </div>

      <main className="flex-1 py-6 px-4 sm:px-6 lg:px-8 print:p-0 print:bg-white">
        <div className="max-w-[850px] mx-auto print:max-w-none rapport-print-wrapper">

          {/* Bannière brouillon */}
          {dossier?.statut === 'brouillon' && (role === 'laborantin' || role === 'super_admin') ? (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-300 rounded-xl flex items-center gap-3 print:hidden">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-draft-line text-amber-600 text-lg"></i>
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-800">Rapport en brouillon — non finalisé</p>
                <p className="text-xs text-amber-600 mt-0.5">
                  Vérifiez les résultats puis cliquez sur <strong>Imprimer</strong> pour finaliser et imprimer, ou <strong>Envoyer au médecin</strong> pour finaliser sans imprimer.
                </p>
              </div>
            </div>
          ) : null}

          {/* Boutons action (masqués à l'impression) */}
          <div className="flex items-center justify-between mb-6 print:hidden">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/laborantin/saisir-analyses')}
                className="flex items-center gap-2 text-gray-600 hover:text-teal-600 transition-colors text-sm font-medium cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                Nouvelle saisie
              </button>
            </div>
            <LabNavigation />
            <div className="flex items-center gap-2">
              {dossier?.statut === 'brouillon' && (role === 'laborantin' || role === 'super_admin') ? (
                <>
                  {/* Message de statut */}
                  {finalizeMessage && (
                    <span className={`text-sm mr-2 ${
                      finalizeMessage.includes('Erreur') ? 'text-red-600' : 'text-teal-600'
                    }`}>
                      {finalizeMessage}
                    </span>
                  )}

                  {/* Bouton Envoyer au médecin (finalise sans imprimer) */}
                  <button
                    onClick={() => finalizeDossier(false)}
                    disabled={finalizing}
                    className="px-4 py-2 bg-teal-600 text-white rounded-full hover:bg-teal-700 transition-all text-sm font-medium whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                    title={!dossier.bon_laboratoire_id ? "Finaliser l'analyse directe" : "Finaliser et envoyer les notifications au prescripteur"}
                  >
                    {finalizing ? (
                      <><i className="ri-loader-4-line animate-spin"></i>Finalisation...</>
                    ) : (
                      <><i className="ri-send-plane-line"></i>Envoyer au médecin</>
                    )}
                  </button>

                  {/* Bouton Imprimer (finalise + imprime) */}
                  <button
                    onClick={() => finalizeDossier(true)}
                    disabled={finalizing}
                    className="px-4 py-2 bg-white border-2 border-teal-600 text-teal-700 rounded-full hover:bg-teal-50 transition-all text-sm font-medium whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                    title="Finaliser le rapport et imprimer"
                  >
                    <i className="ri-printer-line"></i>
                    Imprimer
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => window.print()}
                    className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-all text-sm font-medium whitespace-nowrap cursor-pointer flex items-center gap-1.5"
                  >
                    <i className="ri-printer-line"></i>
                    Imprimer
                  </button>
                  <button
                    onClick={() => navigate('/laborantin/saisir-analyses')}
                    className="px-4 py-2 bg-teal-600 text-white rounded-full hover:bg-teal-700 transition-all text-sm font-medium whitespace-nowrap cursor-pointer flex items-center gap-1.5"
                  >
                    <i className="ri-add-line"></i>
                    Nouvelle saisie
                  </button>
                </>
              )}
            </div>
          </div>

          {/* ====== DOCUMENT DE LABORATOIRE AVEC EN-TÊTE OFFICIEL ====== */}
          <div className="relative bg-white print:bg-white print:shadow-none border border-gray-200 rounded-lg">
            <div className="px-10 pt-8 pb-8 print:px-10 print:pt-8 print:pb-8">

              {/* INFOS PATIENT + DEMANDEUR */}
              <div className="mb-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                  <div className="space-y-1.5">
                    <div className="flex">
                      <span className="w-24 font-semibold text-gray-700 flex-shrink-0">Nom :</span>
                      <span className="font-medium text-gray-900 uppercase">{patient?.nom || '—'}</span>
                    </div>
                    <div className="flex">
                      <span className="w-24 font-semibold text-gray-700 flex-shrink-0">Prénom :</span>
                      <span className="font-medium text-gray-900">{patient?.prenom || '—'}</span>
                    </div>
                    <div className="flex">
                      <span className="w-24 font-semibold text-gray-700 flex-shrink-0">Âge :</span>
                      <span className="font-medium text-gray-900">{getAgeLabel(patientAge)}</span>
                    </div>
                    <div className="flex">
                      <span className="w-24 font-semibold text-gray-700 flex-shrink-0">Sexe :</span>
                      <span className="font-medium text-gray-900">
                        {patient?.sexe === 'F' ? 'Féminin' : patient?.sexe === 'M' ? 'Masculin' : '—'}
                      </span>
                    </div>
                    <div className="flex">
                      <span className="w-24 font-semibold text-gray-700 flex-shrink-0">N° Dossier :</span>
                      <span className="font-medium text-gray-900">{patient?.numero_dossier || '—'}</span>
                    </div>
                    <div className="flex">
                      <span className="w-24 font-semibold text-gray-700 flex-shrink-0">Téléphone :</span>
                      <span className="font-medium text-gray-900">{patient?.telephone || '—'}</span>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex">
                      <span className="w-32 font-semibold text-gray-700 flex-shrink-0">Prescripteur :</span>
                      <span className="font-medium text-gray-900">
                        {!bonLaboratoire ? (
                          dossier?.prescripteur_externe_nom ? (
                            <span className="flex items-center gap-1.5 text-teal-700">
                              <i className="ri-flask-line"></i>
                              {dossier.prescripteur_externe_nom} <span className="text-teal-500 text-xs">(externe)</span>
                            </span>
                          ) : (
                            <span className="flex items-center gap-1.5 text-teal-700">
                              <i className="ri-flask-line"></i>
                              Analyse directe (prescription externe)
                            </span>
                          )
                        ) : bonLaboratoire.prescripteur_type === 'sage_femme' ? (
                          <>Sage-femme {bonLaboratoire.sage_femme?.prenom || ''} {bonLaboratoire.sage_femme?.nom || ''}</>
                        ) : (
                          <>{bonLaboratoire.medecin ? `Dr. ${bonLaboratoire.medecin.prenom} ${bonLaboratoire.medecin.nom}` : 'Non spécifié'}</>
                        )}
                      </span>
                    </div>
                    <div className="flex">
                      <span className="w-32 font-semibold text-gray-700 flex-shrink-0">Type d'analyse :</span>
                      <span className="font-medium text-gray-900">{bonLaboratoire?.type_analyse || 'Analyses de laboratoire'}</span>
                    </div>
                    <div className="flex">
                      <span className="w-32 font-semibold text-gray-700 flex-shrink-0">Date de prélèvement :</span>
                      <span className="font-medium text-gray-900">{formaterDateLettre(dossier?.date_analyse)}</span>
                    </div>
                    <div className="flex">
                      <span className="w-32 font-semibold text-gray-700 flex-shrink-0">Réf. Dossier :</span>
                      <span className="font-medium text-gray-500 text-xs">{dossier?.id ? dossier.id.slice(0, 8).toUpperCase() : '—'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Ligne de séparation */}
              <div className="mb-4 print:hidden"></div>
              <div className="hidden print:block mb-5"></div>

              {/* TABLEAU DES RÉSULTATS PAR CATÉGORIE */}
              {categoriesOrdonnees.map((categorie) => {
                const analyses = resultatsParCategorie[categorie];
                return (
                  <div key={categorie} className="mb-4 last:mb-0">
                    {/* Titre catégorie */}
                    <div className="bg-gray-100 px-4 py-1.5">
                      <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wider">
                        {categorie}
                      </h3>
                    </div>

                    {/* Tableau */}
                    <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[560px]">
                      <thead>
                        <tr>
                          <th className="text-left px-4 py-1.5 text-xs font-bold text-gray-700 uppercase w-[45%]">
                            Tests
                          </th>
                          <th className="text-left px-3 py-1.5 text-xs font-bold text-gray-700 uppercase w-[25%]">
                            Résultats
                          </th>
                          <th className="text-left px-3 py-1.5 text-xs font-bold text-gray-700 uppercase w-[30%]">
                            Normes
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {analyses.map((resultat) => {
                          const ref = getValeursReference(resultat.analyse, patientAge, patient?.sexe || null);
                          const statut = getResultatStatut(resultat.valeur, ref.min, ref.max);
                          const isNormal = statut === 'normal';
                          const isBas = statut === 'bas';
                          const isTexte = resultat.valeur === null && resultat.valeur_texte !== null;
                          const typeAnalyse = detecterTypeAnalyse(resultat.analyse?.code || '');
                          const estStructure = typeAnalyse === 'nfs' || typeAnalyse === 'atb' || typeAnalyse === 'ecbu';
                          const lignesDonnees = formatDonneesResultat(typeAnalyse, resultat.donnees);
                          const hasFichier = !!resultat.fichier_url;

                          let normeText = '';
                          if (ref.min !== null && ref.max !== null) {
                            normeText = `${ref.min} – ${ref.max}`;
                          } else if (ref.max !== null) {
                            normeText = `< ${ref.max}`;
                          } else if (ref.min !== null) {
                            normeText = `> ${ref.min}`;
                          } else {
                            normeText = '—';
                          }

                          const valeurText = isTexte
                            ? resultat.valeur_texte!
                            : resultat.valeur !== null
                              ? `${resultat.valeur} ${resultat.analyse?.unite || ''}`
                              : (hasFichier ? 'Fichier joint' : '—');

                          const anterieurs = resultatsAnterieurs.get(resultat.analyse?.id) || [];

                          if (estStructure && lignesDonnees.length > 0) {
                            return (
                              <Fragment key={resultat.id}>
                                <tr>
                                  <td colSpan={3} className="px-4 pt-2 pb-1 bg-gray-50/60">
                                    <span className="font-semibold text-gray-800">{resultat.analyse?.nom || 'Analyse'}</span>
                                    <span className="text-[10px] text-gray-500 ml-1">({resultat.analyse?.code})</span>
                                  </td>
                                </tr>
                                <tr>
                                  <td colSpan={3} className="px-4 pb-2">
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-1.5">
                                      {lignesDonnees.map((d, i) => (
                                        <div key={i} className="bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-1.5">
                                          <div className="text-[10px] text-gray-500">{d.label}</div>
                                          <div className="text-sm font-semibold text-gray-800">{d.valeur || '—'}</div>
                                        </div>
                                      ))}
                                    </div>
                                  </td>
                                </tr>
                              </Fragment>
                            );
                          }

                          return (
                            <>
                            <tr
                              key={resultat.id}
                              className={`${
                                !isNormal && !isTexte && resultat.valeur !== null ? 'bg-red-50/40' : ''
                              }`}
                            >
                              <td className="px-4 py-2">
                                <span className="font-semibold text-gray-800">{resultat.analyse?.nom || 'Analyse'}</span>
                                <span className="text-[10px] text-gray-500 ml-1">({resultat.analyse?.code})</span>
                              </td>
                              <td className="px-3 py-2">
                                <span className={`font-bold ${isTexte ? 'text-gray-900 italic' : resultat.valeur === null ? 'text-gray-900' : isNormal ? 'text-gray-900' : isBas ? 'text-amber-700' : 'text-red-700'}`}>
                                  {valeurText}
                                </span>
                                {hasFichier && (
                                  <a
                                    href={resultat.fichier_url!}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="ml-2 inline-flex items-center gap-1 text-[11px] font-medium text-teal-700 hover:text-teal-900 underline"
                                  >
                                    <i className="ri-file-pdf-2-line"></i>Voir le fichier
                                  </a>
                                )}
                                {isTexte && (
                                  <span className="ml-2 text-[10px] font-medium text-gray-500">(Texte)</span>
                                )}
                                {resultat.valeur !== null && !isNormal && !isTexte && (
                                  <span className={`ml-2 text-[10px] font-bold ${isBas ? 'text-amber-600' : 'text-red-600'}`}>
                                    {isBas ? '↓ BAS' : '↑ HAUT'}
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-gray-600">
                                {isTexte || resultat.valeur === null ? '—' : `${normeText} ${resultat.analyse?.unite || ''}`}
                              </td>
                            </tr>

                            {/* Résultats antérieurs */}
                            {anterieurs.length > 0 && (
                              <>
                                <tr key={`${resultat.id}-prev-header`} className="">
                                  <td colSpan={3} className="px-4 py-1 bg-amber-50/50">
                                    <span className="text-[11px] font-semibold text-amber-700 flex items-center gap-1">
                                      <i className="ri-history-line"></i>
                                      Résultats antérieurs — {resultat.analyse?.nom}
                                    </span>
                                  </td>
                                </tr>
                                {anterieurs.map((ant, idx) => {
                                  const antValeurText = ant.valeur === null && ant.valeur_texte
                                    ? ant.valeur_texte
                                    : ant.valeur !== null
                                      ? `${ant.valeur} ${ant.analyse_unite || ''}`
                                      : '—';
                                  return (
                                    <tr key={`${resultat.id}-prev-${ant.id}`} className="bg-amber-50/30">
                                      <td className="px-4 py-1.5 pl-8">
                                        <span className="text-xs text-gray-500">{formaterDateHeure(ant.dossier_date)}</span>
                                      </td>
                                      <td className="px-3 py-1.5">
                                        <span className="text-xs font-medium text-gray-700">{antValeurText}</span>
                                      </td>
                                      <td className="px-3 py-1.5">
                                        <span className="text-[10px] text-gray-400">N-{idx + 1}</span>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </>
                            )}
                            </>
                          );
                        })}
                      </tbody>
                    </table>
                    </div>
                  </div>
                );
              })}

              {/* CONCLUSION DU BIOLOGISTE MÉDICAL */}
              {dossier?.conclusion && (
                <div className="mt-5 pt-4">
                  <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wider mb-2">
                    Conclusion du Biologiste médical
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm text-gray-800 whitespace-pre-line leading-relaxed">
                      {dossier.conclusion}
                    </p>
                  </div>
                </div>
              )}

              {/* FAIT À COTONOU + BIOLOGISTE */}
              <div className="mt-6 pt-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4">
                  <div className="text-sm text-gray-800">
                    <p className="font-semibold">
                      Fait à Cotonou le, {formaterDateLettre(dossier?.date_analyse)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-600 mb-1">Le Biologiste médical</p>
                    <p className="text-sm font-semibold text-gray-800">
                      {laborantin ? `${laborantin.prenom} ${laborantin.nom}` : '—'}
                    </p>
                  </div>
                </div>
              </div>

              {/* QR CODE AUTHENTICITÉ */}
              {qrDataUrl && (
                <div className="mt-6 pt-4 flex justify-center">
                  <img
                    src={qrDataUrl}
                    alt="QR Code d'authenticité"
                    className="w-16 h-16"
                  />
                </div>
              )}

            </div>
          </div>

        </div>
      </main>

      <div className="print:hidden">
        <Footer />
      </div>

      {/* Styles d'impression */}
      <style>{`
        @media print {
          @page { size: A4; margin: 0; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .rapport-print-wrapper { padding-top: 3cm; padding-bottom: 3cm; }
        }
      `}</style>
    </div>
  );
}